import { Integer } from '@type-r/ext-types';
import { attributes, AttributesMixin, Collection, define, mixins, Model, subsetOf, type } from '@type-r/models';
import { Dictionary } from 'lodash';
import { asCurrency, asDate, asTimelessDate, BooleanFilter, DateRangeFilter, getDateOnly, MultiSelectFilter, TextFilter } from 'lqx-react';
import { InmatchPermission, InmatchSupplierRead, InmatchSupplierWrite, localUserInfo, ParticipantRoles, getUsersByPermissions, globalStore, CompanyModel, PlatformCompany } from 'api';
import { createdUpdatedAttrs, matchString, FixedNumber, roundNumber, floatsLessThanOrEqual } from 'api/common';
import { fetchJson, fetchResponse, restfulIO } from 'api/endpoint';

import { PlatformCompanyConfig, root } from './common';
import { ConversationMixin, InmatchConversation } from './conversation';
import { filterByFiscalPeriod, filterByFiscalYear, FiscalCalendar, FiscalCalendarDto } from './fiscal-calendar';
import { RecsInvoice } from './invoice';
import { RemittanceAdvice } from './remittance-advice';
import { Document } from "api/uploads";
import { getAuthToken, getGeneralPlatformCode } from 'api/embedded-auth';
import { Can } from 'src/app/services/can';
import { UserEntitlementCatalogs } from 'src/app/models/UserEntitlements';
export interface InvoiceMatch extends AttributesMixin<typeof InvoiceMatch>{}

const dummyInvoice = { unreconciledAmount : 0 } ;
@define
export class InvoiceMatch extends Model {
    static attributes = {
        //id : String,
        ...createdUpdatedAttrs,
        invoiceId : String,
        remittanceAdviceId : Integer,
        isManual : type( Boolean ).dontSave,
        originType : type( String ).dontSave,
        appliedAmount : type(FixedNumber(2)).check(function (x) {return !this.invoice || floatsLessThanOrEqual(x + this.discountAmount + this.writeOff, (this.invoice || dummyInvoice).unreconciledAmount);}), // amount
        source : type( String ).null.dontSave, // remittance advice source, or null for a manual match

        discountAmount : type(FixedNumber(2))
            .check(function (x) {return floatsLessThanOrEqual(x + this.writeOff, (this.invoice || dummyInvoice).unreconciledAmount);})
            .watcher('updateAppliedAmount'),

        writeOff: type(FixedNumber(2))
            .check(function (x) {return floatsLessThanOrEqual(x + this.discountAmount, (this.invoice || dummyInvoice).unreconciledAmount);})
            .watcher('updateAppliedAmount'),
        invoiceNumber: String,
        isActive : type(Boolean).dontSave,
        recommendationId: type(Number).dontSave,
    }

    filter(text: string) {
        return !text || matchString(this.invoice.invoiceNumber, text) 
            || matchString(this.invoice.invoicedAmount.toString(), text)
            || matchString(asTimelessDate(this.invoice.invoiceDueDate), text)
            || matchString(this.invoice.additionalReferenceIdentifier ? this.invoice.additionalReferenceIdentifier : '', text);
    }

    filterUnfunded(text: string) {
        return !text || matchString(this.invoiceNumber, text) 
            || matchString(this.appliedAmount.toString(), text);
    }

    get invoice(){
        return ( this.getOwner() as any ).invoices.get( this.invoiceId );
    }

    get payment() : Payment {
        return ( this.getOwner() as any );
        
    }
    set invoice( x : RecsInvoice ){
        this.invoiceId = x ? x.id : null;
    }

    updateAppliedAmount(){
        this.appliedAmount = Math.max(this.invoice.unreconciledAmount - this.discountAmount - this.writeOff, 0);
    }

    setWriteOffMaxValue() {
        this.writeOff = this.invoice.unreconciledAmount - this.discountAmount;
    }

    setDiscountMaxValue() {
        this.discountAmount = this.invoice.unreconciledAmount - this.writeOff;
    }
}

export interface Deductables extends AttributesMixin<typeof Deductables> {}
@define
export class Deductables extends Model {
    static attributes = {
        processingFees: FixedNumber(2), // TODO: find a good way to assign payment.defaultFees by default
        fxGainLoss: FixedNumber(2),
        overpayment: FixedNumber(2),
        overpaymentSendToPayor: FixedNumber(2),
        creditNote: {
            number : String,
            reason : String,
            amountApplied : FixedNumber(2),
        },
        amountSettleOffPlatform: FixedNumber(2),
        marketingDiscount: FixedNumber(2),
    };

    get amount() {
        return this.processingFees + this.creditNote.amountApplied - this.fxGainLoss - this.overpayment - this.overpaymentSendToPayor - this.amountSettleOffPlatform + this.marketingDiscount;
    }

    get discount() {
        return (this.getOwner() as Payment).matches.reduce((x : number, inv ) => x + inv.discountAmount , 0);
    }

    get writeOff() {
        return (this.getOwner() as Payment).matches.reduce((x : number, inv ) => x + inv.writeOff , 0);
    }
};

export interface Recommendation extends AttributesMixin<typeof Recommendation> {}

@define
export class Recommendation extends Model {
    static endpoint = restfulIO(root + 'recommendation');
    
    static attributes = {
        ...createdUpdatedAttrs,
        paymentId: type(Integer).value(undefined),
        range: String,
        originType: String,
        matches : type([InvoiceMatch]).required,
        invoices : type([ RecsInvoice ]).dontSave,
        rangeTitle: String,
        originTitle: String,
    }

    get amount() {
        return this.matches.reduce((t: number, inv) => t + inv.appliedAmount, 0)
    }

    get origin() {
        const inv = this.matches.first().invoice;
        if (this.originType === 'PayorPayee' || this.originType === 'Greedy') {
            return `${inv.supplierName} - ${inv.customerName}`;
        } else if (this.originType === 'ProgramId') {
            const metadata = (inv.metadata as any);
            return metadata && metadata.transactionMetaData ? metadata.transactionMetaData.facilityName : ''
        } 

        return '';
    }
}

export interface Payment extends AttributesMixin<typeof Payment>, ConversationMixin {}

@define
export class Payment extends Model {
    /**
     * GET /api/v2/recs/payments fetch the list of unreconciled payments.
     */
    static endpoint = restfulIO( root + 'payments', /*{
        mockData : [ { amount : 10, invoices : [ { id : 1 }, { id : 2 } ] }, { amount : 10 }, { amount : 10 } ],
        simulateDelay : 2000
    }*/ );

    static attributes = {
        ...createdUpdatedAttrs,
        remittanceAdviceId : Integer,
        remittanceAdvices : type( [ RemittanceAdvice ] )
            .check( () => true )
            .dontSave    
            .endpoint( restfulIO( './remittanceAdvices' ) ),

        paymentInfoInvoices : subsetOf( 'invoices' ),
        amount : Number,
        typeId : Integer,
        // customerId : Integer,
        // supplierId : Integer,
        unreconciledAmount: Number,
        conversationId : Integer,
        valueDate : Date,
        statementDate : Date,
        relationshipId : Integer,
        metadata : null, //???
        currency : String,
        baiCode : String,

        isActive : Boolean,
        isReconciled : Boolean,

        deductables: Deductables,

        /**
         * list of unreconciled matches for the payment shall be fetched as a part of the payment.
         * GET     /api/v2/payments/{id}/matches - fetch the list of unreconciled matches for the payment
         * POST    /api/v2/payments/{id}/matches - create the match
         * DESTROY /api/v2/payments/{id}/matches/{matchId} - remove the match
         */
        matches : type([ InvoiceMatch ])
                    .endpoint( restfulIO('./matches' ) ),

        invoices : type([ RecsInvoice ]).dontSave.endpoint( restfulIO( './invoices' ) ),
        paymentDetail : {
            paymentId : Number,
            creditorName : String,
            creditorBankName : String,
            creditorAccount : String,
            debtorName : String,
            debtorBankName: String,
            debtorAccount : String,
            exchangeRate : String,
            id : String,
            remittanceInfoUnstructured : String,
            entryReferenceNumber : String,
            accountServicerReference : String,
            source: String,
            endToEndId : String,
            additionalEntryInfo : String,
            isDuplicate : Boolean
        },
        // payorAgentId : Integer,
        
        uploads: type([Document]).dontSave.endpoint(restfulIO("./uploads")),
        remittanceAdvice : type(RemittanceAdvice)
            .check(() => true)
            .dontSave
            .endpoint(restfulIO(root + 'remittance-advice')),
        recommendations : type( [ Recommendation ] )
            .check( () => true )
            .dontSave    
            .endpoint( restfulIO( root + 'recommendation' ) ),
        platformCode: type(String).dontSave,
        matchesAggregation: {
            count: Number,
            unfundedCount: Number,
            remittanceInfoCount: Number,
            remittanceAdviceCount: Number,
            heuristicCount: Number,
            manualCount: Number,
            greedyCount: Number,
            recommendationsCount: Number
        },
        supplierPlatformCompanyId: Number,
        customerPlatformCompanyId: Number,
        payorAgentPlatformCompanyId: Number,
        creditorBankName : String,
        debtorBankName: String,
        creditorAccount : String,
        debtorAccount : String,
        customerConfigs: type([PlatformCompanyConfig]).dontSave,
    }

    get age(){
        return Math.floor( ( Date.now() - this.valueDate.getTime() ) / 86400000 )
    }

    get color() {
        return this.matchesAggregation && this.matchesAggregation.count ? ( this.isWithinTolerance() ? 'green' : 'yellow' ) : 'red';
    }

    get cashApplied(){
        return this.matches && this.matches.length
            ? this.matches.reduce( ( acc : number, x ) => acc + x.appliedAmount + x.discountAmount, 0)
            : 0;
    }

    get cashReceived(){
        return this.amount;
    }

    get finalCashApplied(){
        const { deductables } = this;
        const finalCashApplied = roundNumber(this.cashApplied - deductables.processingFees - deductables.creditNote.amountApplied + deductables.fxGainLoss);
        return finalCashApplied === -0 ? 0 : finalCashApplied;
    }

    get displayAmount(){
        return this.unreconciledAmount;
    }

    get tolerance() {
        if (!this.customerConfigs || this.customerConfigs.length === 0) {
            return 0;
        }

        const customConfig = this.customerConfigs.last();
        const defaultConfig = this.customerConfigs.first();

        const customAmount = this.getToleranceAmount(customConfig);
        if (customAmount !== undefined) {
            return customAmount;
        }

        const defaultAmount = this.getToleranceAmount(defaultConfig);
        if (defaultAmount !== undefined) {
            return defaultAmount;
        }

        return 0;
    }

    private getToleranceAmount(config: any): number | undefined {
        if (!config) return undefined;
    
        const percentAmount =
            config.recommendationTolerancePercent !== undefined
                ? Number(config.recommendationTolerancePercent) * this.amount / 100
                : undefined;
    
        return config.recommendationToleranceAmount !== undefined
            ? config.recommendationToleranceAmount
            : percentAmount;
    }

    get defaultFees() {
        return 0; // TODO: get from relationship table
    }

    get delta() {
        const delta = roundNumber(this.unreconciledAmount - this.cashApplied + this.deductables.amount ) ;
        return delta === -0 ? 0 : delta;
    }

    get daysDue() {
        return Math.floor( ( Date.now() - this.statementDate.getTime() ) / 86400000 );
    }

    // get customer() : CompanyModel {
    //     return globalStore.companies.get(this.customerId as any);
    // }

    get customer() : PlatformCompany {
        return globalStore.platformCompanies.get(this.customerPlatformCompanyId as any);
    }

    // get supplier() : CompanyModel {
    //     return globalStore.companies.get(this.supplierId as any);
    // }
    
    get supplier() : PlatformCompany {
        return globalStore.platformCompanies.get(this.supplierPlatformCompanyId as any);
    }

    get customerName() : string {
        return this.customer ? this.customer.name : `Customer #${this.customerPlatformCompanyId}`;
    }

    get supplierName() : string {
        return this.supplier ? this.supplier.name : `Supplier #${this.supplierPlatformCompanyId}`;
    }

    get payorAgent() : PlatformCompany {
        return globalStore.platformCompanies.get(this.payorAgentPlatformCompanyId as any);
    }

    hasManualMatches(){
        return this.matches.some( x => x.isManual );
    }

    isWithinTolerance(delta = this.delta) {
        return delta >= -this.tolerance - 0.0001 && delta < 0.00001 ;
    }

    async linkRa( raId : number ){
        await fetchJson( "POST", `/api/v2/recs/payments/${this.id}/remittanceAdviceId`, raId as any )
        await this.fetch();
    }

    toJSON( options? ){
        return {
            id : this.id,
            deductables: this.deductables.toJSON()
        }
    }

    filter( category,
            filter: UnreconciledPaymentFilters,
            supplierPlatformCompanyIdsWithSelectedName: number[],
            customerPlatformCompanyIdsWithSelectedName: number[]
        ) : boolean {

        let result = this.unreconciledAmount > 0 && (
            category === "matched" ? Boolean( this.matchesAggregation && this.matchesAggregation.count ) :
            category === "unmatched" ? (!this.matchesAggregation || !this.matchesAggregation.count) :
            true
        ) && (!filter.filterAll || matchString( this.supplierName, filter.filterAll ) || matchString( this.customerName, filter.filterAll )
            || matchString(String(this.unreconciledAmount), filter.filterAll)
            || matchString(asCurrency(this.unreconciledAmount), filter.filterAll)
            || matchString( asTimelessDate(this.statementDate), filter.filterAll )
            || matchString( this.creditorBankName, filter.filterAll )
            || matchString( this.debtorBankName, filter.filterAll )
            || matchString(String(this.daysDue), filter.filterAll)
        ) && (!filter.statementDate.from || !filter.statementDate.to || (this.statementDate >= filter.statementDate.from && this.statementDate <= filter.statementDate.to))
          && (!filter.valueDate.from || !filter.valueDate.to || (this.valueDate >= filter.valueDate.from && this.statementDate <= filter.valueDate.to))
          && this.filterByMultiSelect(filter.supplierBankAccount, this.creditorAccount)
          && this.filterByMultiSelect(filter.customerBankAccount, this.debtorAccount)
          && (!filter.outstandingAmount.value || matchString(asCurrency(this.amount), filter.outstandingAmount.value) || matchString(String(this.amount), filter.outstandingAmount.value));

        if (result && filter.supplierName.selected && filter.supplierName.selected.length) {

            result = result && supplierPlatformCompanyIdsWithSelectedName.includes(this.supplierPlatformCompanyId);
        }

        if (result && filter.customerName.selected && filter.customerName.selected.length) {

            result = result && customerPlatformCompanyIdsWithSelectedName.includes(this.customerPlatformCompanyId);
        }

        return result;
    }

    async fetch(){
        const fetches : Promise<any>[] = [super.fetch(), this.invoices.fetch()];

        if (this.remittanceAdviceId){
            this.remittanceAdvice.id = this.remittanceAdviceId as any;
            fetches.push(this.remittanceAdvice.fetch());
        }

        await Promise.all(fetches);
        
        return this;
    }

    isMatched( { id } : RecsInvoice ){
        return this.matches.some( x => x.invoiceId == id );
    }

    async getRolesByPermission(permission: InmatchPermission) {
        const users = await getUsersByPermissions(this.conversationId, {Inmatch: [permission]});

        const emails = users.map(u => u.userName);

        return emails;
    }

    private filterByMultiSelect(multiSelect : MultiSelectFilter, value) : Boolean {
        return !multiSelect || !multiSelect.selected || !multiSelect.selected.length
            || multiSelect.selected.includes(value);
    }

    get isSupplier(): boolean {
        return localUserInfo.hasSomePermissions(
            "Inmatch",
            ParticipantRoles.Supplier,
            InmatchSupplierRead,
            this.conversationId
        );
    }

    get isSupplierWrite(): boolean {
        return localUserInfo.hasSomePermissions(
            "Inmatch",
            ParticipantRoles.Supplier,
            InmatchSupplierWrite,
            this.conversationId
        );
    }

    get isSupplierManager(): boolean {
        // FIXME: Everyone is a manager!!... Until users are added
        return true;
        return localUserInfo.hasSomePermissions(
            "Inmatch",
            ParticipantRoles.Supplier,
            [InmatchPermission.SupplierManager],
            this.conversationId
        );
    }

    get isSupplierAnalyst(): boolean {
        return localUserInfo.hasSomePermissions(
            "Inmatch",
            ParticipantRoles.Supplier,
            [InmatchPermission.SupplierAnalyst],
            this.conversationId
        );
    }

    get isSupplierCollector(): boolean {
        return localUserInfo.hasSomePermissions(
            "Inmatch",
            ParticipantRoles.Supplier,
            [InmatchPermission.SupplierCollector],
            this.conversationId
        );
    }

    async save( options? : { params : { file, comment } } ){
        const { file, comment } = options.params;
        const url = (this.getEndpoint() as any).getUrl(this);
        const httpVerb = this.id ? 'PUT' : 'POST';

        const formData = new FormData();

        const payment = this.toJSON();
        const changedMatches = this.matches.models.filter(match => match.changedAttributes());
        payment['matches'] = changedMatches;
        formData.append("request", JSON.stringify({...payment, comment}));
        if (file) {
            formData.append("file", file, file.name);
        }
        formData.append("comment", comment);

        const response = await fetch(url, {
            method: httpVerb,
            headers: new Headers({
                "Authorization": "Bearer " + getAuthToken(),
                "X-Platform-Code": getGeneralPlatformCode()
            }),
            body : formData
        });

        if(response.status < 200 || response.status >= 300){
            const errorResponse = await response.text();
            throw new Error(errorResponse || response.statusText || `HTTP Error ${response.status}`);
        }

        const json = await response.json();
        this.set(json);
        return this;
    }
}

const statuses = ['Booked', 'Manual', 'Automatic'];
export type ReconcilitionStatusType = typeof statuses[number];

export interface Reconciliation extends AttributesMixin<typeof Reconciliation> {}

@define
export class Reconciliation extends Model {
    static endpoint = restfulIO( root + 'reconciliation' );

    static attributes = {
        ...createdUpdatedAttrs,
        conversationId : Integer,
        paymentId : String,
        isManual : Boolean,
        valueDate : Date,
        statementDate : Date,
        cashReceived : Number,
        deductables : Deductables,
        cashApplied : Number,
        matches : [InvoiceMatch],
        bookedOn : type(Date).value(undefined),

        // Only those invoices that appears in matches.
        invoices : type([ RecsInvoice ]).dontSave,
        paymentDetail : {
            paymentId : Number,
            creditorName : String,
            creditorBankName : String,
            creditorAccount : String,
            debtorName : String,
            debtorBankName: String,
            debtorAccount : String,
            exchangeRate : String,
            id : String,
            remittanceInfoUnstructured : String,
            entryReferenceNumber : String,
            accountServicerReference : String,
            endToEndId : String,
            additionalEntryInfo : String,
            isDuplicate : Boolean
        },
        supplierId : Integer,
        customerId : Integer,
        currency : String,
        canBeBookedByUser : type(Boolean).dontSave,
        payorAgentId: type(Number).dontSave,
        matchesAggregation: {
            count: Number,
            amount: Number,
            autoMatchedCount: Number,
            unfundedInvAmount: Number,
            isThreeWayMatch: Boolean,
            anyManualChange : Boolean,
        },
        supplierPlatformCompanyId: Number,
        customerPlatformCompanyId: Number,
    }

    filter( filter: ReconciliationFilters, conversationByCustExternalId ) : boolean {
        const bookedDate = new Date(this.bookedOn);
        bookedDate && bookedDate.setHours(0, 0, 0, 0);

        const reconciliationDate = new Date(this.createdOn);
        reconciliationDate && reconciliationDate.setHours(0, 0, 0, 0);

        return (!filter.filterAll || matchString( this.customerName, filter.filterAll )
                || matchString( this.supplierName, filter.filterAll )
                || matchString( String(this.cashApplied), filter.filterAll )
                || matchString( String(this.cashReceived), filter.filterAll ))
            && this.filterByMultiSelect(filter.supplierName, this.supplierName)
            && this.filterByMultiSelect(filter.customerName, this.customerName)
            && this.filterByMultiSelect(filter.supplierBankAccount, this.paymentDetail.creditorAccount)
            && this.filterByMultiSelect(filter.customerBankAccount, this.paymentDetail.debtorAccount)
            && (!filter.cashReceived.value || matchString(asCurrency(this.cashReceived), filter.cashReceived.value) || matchString(String(this.cashReceived), filter.cashReceived.value))
            && (!filter.cashApplied.value || matchString(asCurrency(this.cashApplied), filter.cashApplied.value) || matchString(String(this.cashApplied), filter.cashApplied.value))
            && (!filter.valueDate.from || !filter.valueDate.to || (this.valueDate >= filter.valueDate.from && this.valueDate <= filter.valueDate.to))
            && (!filter.statementDate.from || !filter.statementDate.to || (this.statementDate >= filter.statementDate.from && this.statementDate <= filter.statementDate.to))
            && (!filter.reconciliationDate.from || !filter.reconciliationDate.to || (reconciliationDate >= filter.reconciliationDate.from && reconciliationDate <= filter.reconciliationDate.to))
            && (!filter.bookedDate.from || !filter.bookedDate.to || (bookedDate >= filter.bookedDate.from && bookedDate <= filter.bookedDate.to))
            && (!filter.reconciledOrBookedToday.value || (matchString( asDate(this.bookedOn), asDate(new Date())))
                || (matchString( asDate(this.createdOn), asDate(new Date()))))
            && (!filter.customerExternalId || !filter.customerExternalId.value || conversationByCustExternalId.includes(this.conversationId));
    }

    get finalCashApplied(){
        return this.cashApplied;
    }

    get amount(){
        return this.matchesAggregation.amount;
    }

    get displayAmount(){
        return this.cashApplied;
    }

    get tolerance() {
        return 60; // TODO: get from relationship table or from payment
    }

    get delta() {
        return this.cashApplied - this.amount;
    }

    isWithinTolerance(delta = this.delta) {
        return true;
    }

    get payment() : Payment {
        return ( this.getStore() as any ).payments.get( this.paymentId );
    }

    set payment( x : Payment ){
        this.paymentId = ( x && x.id ) || null;
    }

    get status() : ReconcilitionStatusType {
        return this.bookedOn ? 'Booked' : this.isManual ? "Manual" : "Automatic"
    }

    get customer() : PlatformCompany {
        return globalStore.platformCompanies.get(this.customerPlatformCompanyId as any);
    }

    get supplier() : PlatformCompany {
        return globalStore.platformCompanies.get(this.supplierPlatformCompanyId as any);
    }
    
    get customerName() : string {
        return this.customer ? this.customer.name : `Customer #${this.customerId}`;
    }

    get supplierName() : string {
        return this.supplier ? this.supplier.name : `Supplier #${this.supplierId}`;
    }

    get payorAgentName() : string {
        const payorAgentCompany = this.payorAgentId && this.getStore()['companies'].get(this.payorAgentId);
        return payorAgentCompany ? payorAgentCompany.name : '';
    }

    get typeOfMatch() : string {
        return this.matchesAggregation.isThreeWayMatch ? '3 Way Match' : '2 Way Match';
    }

    get autoMatchedNumber() : Number {
        return this.matchesAggregation.autoMatchedCount;
    }

    get unfundedInvAmount() : number {
        return this.matchesAggregation.unfundedInvAmount;
    }

    get ableToBook() : Boolean {
        if (Can(UserEntitlementCatalogs.ApproveAndBookRecommendations)) {
            return !this.matchesAggregation.anyManualChange || this.canBeBookedByUser;
        } else {
            return this.canBeBookedByUser;
        }
    }

    async destroy(options?: { params : { file, comment } }) {
        const { file, comment } = options.params;
        const url = (this.getEndpoint() as any).getUrl(this);

        const formData = new FormData();

        if (file) {
            formData.append("file", file, file.name);
        }
        formData.append("comment", comment);

        await fetchJson('DELETE', url, undefined, formData);

        this.dispose();

        return this;
    }

    private filterByMultiSelect(multiSelect : MultiSelectFilter, value) : boolean {
        return !multiSelect || !multiSelect.selected || !multiSelect.selected.length
            || multiSelect.selected.includes(value);
    }
}

export interface ReconciliationFilters extends AttributesMixin<typeof ReconciliationFilters> {}
@define
export class ReconciliationFilters extends Model {
    static attributes = {
        filterAll : '',
        supplierName: MultiSelectFilter,
        customerName: MultiSelectFilter,
        supplierBankAccount: MultiSelectFilter,
        customerBankAccount: MultiSelectFilter,
        cashReceived: TextFilter,
        cashApplied: TextFilter,
        valueDate: DateRangeFilter,
        statementDate: DateRangeFilter,
        reconciliationDate: DateRangeFilter,
        bookedDate: DateRangeFilter,
        reconciledOrBookedToday: BooleanFilter,
        customerExternalId: TextFilter,
    }

    initializeDefaults() {
        this.reconciledOrBookedToday.set( {value: true} );
    }

    initializeOptions(reconciliations: Collection<Reconciliation>, customerExternalIdParam, booked) {
        const companyNames = globalStore.platformCompanies.map(cmp => cmp.name);
        this.supplierName.set({options: Array.from(new Set(companyNames.filter(x => x)))});

        this.customerName.set({options: Array.from(new Set(companyNames.filter(x => x)))});

        const creditorAccounts = reconciliations.map(reconciliation => reconciliation.paymentDetail.creditorAccount);
        this.supplierBankAccount.set({options: Array.from(new Set(creditorAccounts.filter(x => x)))});

        const debtorAccounts = reconciliations.map(reconciliation => reconciliation.paymentDetail.debtorAccount);
        this.customerBankAccount.set({options: Array.from(new Set(debtorAccounts.filter(x => x)))});

        if (customerExternalIdParam) {
            this.customerExternalId.value = customerExternalIdParam;    
        }
        this.customerExternalId.isInitial = true;

        this.bookedDate.isInitial = !booked;        
    }
}

export const CategoryAttrs = {
    amount : Number,
    count : Integer,
    currency : String,
}
@define
export class CategorySummary extends
    attributes({
        unreconciled : {
            ...CategoryAttrs,
            matchedCount : Integer,
        },
        reconciled : { //?? Doesn't make sense. Sum for which period?
            ...CategoryAttrs,
            auto : Integer
        },
        booked : {
            ...CategoryAttrs,
        },
    })
{
    static endpoint = restfulIO( '/api/v2/recs/summary' );
}

export interface UnreconciledPaymentFilters extends AttributesMixin<typeof UnreconciledPaymentFilters> {}
@define
export class UnreconciledPaymentFilters extends Model {
    static attributes = {
        filterAll : '',
        statementDate : DateRangeFilter,
        valueDate : DateRangeFilter,
        supplierBankAccount: MultiSelectFilter,
        customerBankAccount: MultiSelectFilter,
        outstandingAmount: TextFilter,
        supplierId: MultiSelectFilter,
        supplierName: MultiSelectFilter,
        customerName: MultiSelectFilter,
    }

    initializeOptions(payments: Collection<Payment>) {

        const companyNames = globalStore.platformCompanies.map(company => company.name);

        const creditorAccounts = payments.map(payment => payment.creditorAccount);
        this.supplierBankAccount.set({options: Array.from(new Set(creditorAccounts.filter(x => x)))});

        const debtorAccounts = payments.map(payment => payment.debtorAccount);
        this.customerBankAccount.set({options: Array.from(new Set(debtorAccounts.filter(x => x)))});
        this.supplierName.set({options: Array.from(new Set(companyNames.filter(x => x)))});
        this.customerName.set({options: Array.from(new Set(companyNames.filter(x => x)))});
    }
}

export async function execMatchInvoices(matches: Collection<InvoiceMatch>, paymentId, params) {
    const url = `${root}/payments/${paymentId}/matches/multiple${params ? `?${params.toString()}` : ''}`;

    return await fetchJson('POST', url, params ? [] : matches);
}

export async function execUnmatchInvoicesForMultiplePaymnets(paymentInvoiceId, params = undefined) {
    const url = `${root}/payments/matches${params ? `?${params.toString()}` : ''}`;
    await fetchJson('DELETE', url, paymentInvoiceId);
}

export async function execBookPayment(reconciliationId) {
    const url = `${root}/reconciliation/book/${reconciliationId}`;
    return await fetchJson('POST', url);
}

export async function execFlagForReview(paymentId, flag, comment) {
  const paymentFlag = {"flag": flag, "comment": comment };
  const url = `${root}/payments/${paymentId}/flag`;
  return await fetchJson('POST', url, paymentFlag);
}

export async function execReverseReconciliation(reconciliationId) {
    const url = `${root}/reconciliation/reverse-reconciliation/${reconciliationId}`;
    return await fetchJson('POST', url);
}

export async function uploadPaymentFile(paymentId: Number, file: any, comment: string): Promise<number> {
    const formData = new FormData();
    if (file) {
        formData.append("file", file, file.name);
    }
    formData.append("comment", comment);

    const url = root + `payments/${paymentId}/upload-file`;

    return await fetchJson('POST', url, undefined, formData);
}

export async function processPayment(paymentId: Number, facilityId, startDate, endDate) {
    const params = new URLSearchParams();
    if (facilityId) {
        params.append("facilityId", facilityId)
    }
    if (startDate) {
        params.append("startDate", getDateOnly(startDate));
    }
    if (endDate) {
        params.append("endDate", getDateOnly(endDate));
    }
    const url = root + `payments/${paymentId}/process?${params.toString()}`;

    return await fetchResponse('POST', url);
}


export async function execRecommendationSelection(recommendationId, paymentId) {
    const url = `${root}/recommendation/${recommendationId}/${paymentId}`;
    await fetchJson('POST', url);
}

@define
export class Facility extends Model {
	facilityId: number;
	facilityName: string;
}

export async function getFacilities(paymentId) {
    const url = `${root}/payments/${paymentId}/facilities`;

    return await fetchJson('GET', url);
}
  